import React, { useEffect, useMemo, useState } from "react";
import { MailWarning, Send, RefreshCcw, ExternalLink, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice } from "../lib/api";
import AdminLayout from "../components/AdminLayout";

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

function relativeTime(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function AdminRecovery() {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);
  const [filter, setFilter] = useState("active"); // active | sent | recovered | all

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/abandoned-carts", { params: { limit: 500 } });
      setCarts(r.data || []);
    } catch (_) {
      setCarts([]);
      toast.error("Could not load abandoned carts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const triggerSweep = async () => {
    setSweeping(true);
    try {
      const r = await api.post("/admin/abandoned-carts/sweep");
      const sent = r.data?.sent ?? 0;
      toast.success(sent > 0 ? `Sent ${sent} recovery email${sent === 1 ? "" : "s"}.` : "No eligible carts. (Carts must be 1+ hour old.)");
      await load();
    } catch (_) {
      toast.error("Sweep failed.");
    } finally {
      setSweeping(false);
    }
  };

  const counts = useMemo(() => {
    const c = { all: carts.length, active: 0, sent: 0, recovered: 0 };
    carts.forEach((cart) => {
      if (cart.recovered_at) c.recovered += 1;
      else if (cart.email_send_count > 0) c.sent += 1;
      else c.active += 1;
    });
    return c;
  }, [carts]);

  const filtered = useMemo(() => {
    if (filter === "all") return carts;
    return carts.filter((cart) => {
      if (filter === "recovered") return !!cart.recovered_at;
      if (filter === "sent") return !cart.recovered_at && cart.email_send_count > 0;
      return !cart.recovered_at && cart.email_send_count === 0;
    });
  }, [carts, filter]);

  // KPIs across full dataset (not filtered).
  const kpis = useMemo(() => {
    const active = carts.filter((c) => !c.recovered_at);
    const totalAtRisk = active.reduce((sum, c) => sum + (Number(c.total) || 0), 0);
    const recovered = carts.filter((c) => c.recovered_at);
    const recoveredValue = recovered.reduce((sum, c) => sum + (Number(c.total) || 0), 0);
    const emailed = carts.filter((c) => c.email_send_count > 0);
    const recoveryRate = emailed.length > 0
      ? Math.round((emailed.filter((c) => c.recovered_at).length / emailed.length) * 100)
      : 0;
    return { totalAtRisk, recoveredValue, recoveryRate, emailedCount: emailed.length };
  }, [carts]);

  const FILTERS = [
    { key: "active", label: "Pending", count: counts.active },
    { key: "sent", label: "Emailed", count: counts.sent },
    { key: "recovered", label: "Recovered", count: counts.recovered },
    { key: "all", label: "All", count: counts.all },
  ];

  return (
    <AdminLayout
      title="Recovery."
      action={
        <button
          onClick={triggerSweep}
          disabled={sweeping}
          className="btn-ghost !px-5 !py-3"
          data-testid="admin-recovery-sweep"
        >
          {sweeping ? (
            <RefreshCcw size={14} strokeWidth={1.5} className="animate-spin" />
          ) : (
            <Send size={14} strokeWidth={1.5} />
          )}
          {sweeping ? "Sweeping…" : "Run sweep now"}
        </button>
      }
    >
      <div data-testid="admin-recovery-page">
        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--mn-border)] border border-[var(--mn-border)] mb-12">
          <Kpi label="At risk (active)" value={formatPrice(kpis.totalAtRisk)} subtext={`${counts.active + counts.sent} carts`} testid="kpi-at-risk" />
          <Kpi label="Recovered" value={formatPrice(kpis.recoveredValue)} subtext={`${counts.recovered} sales`} testid="kpi-recovered" />
          <Kpi label="Recovery rate" value={`${kpis.recoveryRate}%`} subtext={`${kpis.emailedCount} emailed`} testid="kpi-rate" />
          <Kpi label="Total tracked" value={String(counts.all)} subtext="all-time carts" testid="kpi-total" />
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-recovery-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`recovery-filter-${f.key}`}
              className={`text-xs uppercase tracking-[0.22em] px-4 py-2 border transition-colors ${
                filter === f.key
                  ? "bg-[var(--mn-ink)] text-[var(--mn-bg)] border-[var(--mn-ink)]"
                  : "border-[var(--mn-border-strong)] text-[var(--mn-ink-2)] hover:text-[var(--mn-ink)]"
              }`}
            >
              {f.label} <span className="font-mono-luxe ml-1.5 opacity-70">{f.count}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center text-[var(--mn-ink-2)]" data-testid="admin-recovery-loading">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="border border-dashed border-[var(--mn-border-strong)] py-20 text-center"
            data-testid="admin-recovery-empty"
          >
            <MailWarning size={32} strokeWidth={1.2} className="mx-auto text-[var(--mn-ink-2)]" />
            <p className="font-display text-2xl mt-4">No carts in this view.</p>
            <p className="text-sm text-[var(--mn-ink-2)] mt-2">
              Customers who enter their email at checkout but don't complete will appear here.
            </p>
          </div>
        ) : (
          <div className="border border-[var(--mn-border)]">
            <div className="hidden md:grid grid-cols-[2fr_3fr_1.2fr_1.5fr_1.2fr] gap-6 px-6 py-4 border-b border-[var(--mn-border)] bg-[var(--mn-bg)] text-[10px] uppercase tracking-[0.22em] text-[var(--mn-ink-2)]">
              <div>Customer</div>
              <div>Items</div>
              <div className="text-right">Value</div>
              <div>Captured</div>
              <div>Status</div>
            </div>
            <ul className="divide-y divide-[var(--mn-border)]">
              {filtered.map((c) => (
                <CartRow key={c.id} cart={c} />
              ))}
            </ul>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Kpi({ label, value, subtext, testid }) {
  return (
    <div className="bg-white p-6 md:p-8" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-ink-2)]">{label}</p>
      <p className="font-display text-3xl md:text-4xl mt-2 tracking-tighter">{value}</p>
      <p className="text-xs text-[var(--mn-ink-2)] mt-1">{subtext}</p>
    </div>
  );
}

function CartRow({ cart }) {
  const recovered = !!cart.recovered_at;
  const emailed = cart.email_send_count > 0;
  return (
    <li className="px-6 py-5 md:grid md:grid-cols-[2fr_3fr_1.2fr_1.5fr_1.2fr] md:gap-6 md:items-start" data-testid={`recovery-row-${cart.id}`}>
      {/* Customer */}
      <div className="min-w-0">
        <p className="font-mono-luxe text-sm text-[var(--mn-ink)] truncate" data-testid="recovery-email">
          {cart.email}
        </p>
        <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--mn-ink-2)] mt-1 md:hidden">
          {relativeTime(cart.created_at)}
        </p>
      </div>

      {/* Items (max 3 + overflow chip) */}
      <div className="mt-3 md:mt-0 flex flex-wrap gap-2 min-w-0">
        {(cart.items || []).slice(0, 3).map((it) => (
          <a
            key={it.product_id}
            href={`/product/${it.product_id}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs border border-[var(--mn-border-strong)] px-2 py-1 hover:border-[var(--mn-ink)] transition-colors max-w-[260px]"
          >
            <span className="truncate">{it.name}</span>
            <ExternalLink size={10} strokeWidth={1.5} className="shrink-0 opacity-60" />
          </a>
        ))}
        {(cart.items || []).length > 3 && (
          <span className="text-xs px-2 py-1 text-[var(--mn-ink-2)]">+{cart.items.length - 3} more</span>
        )}
      </div>

      {/* Value */}
      <div className="mt-3 md:mt-0 md:text-right font-mono-luxe text-base text-[var(--mn-ink)]" data-testid="recovery-total">
        {formatPrice(cart.total || 0)}
      </div>

      {/* Captured */}
      <div className="mt-3 md:mt-0 hidden md:block">
        <p className="text-sm">{formatDateTime(cart.created_at)}</p>
        <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--mn-ink-2)] mt-1">
          {relativeTime(cart.created_at)}
        </p>
      </div>

      {/* Status */}
      <div className="mt-3 md:mt-0">
        {recovered ? (
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] bg-[var(--mn-ink)] text-[var(--mn-bg)] px-2.5 py-1.5">
            <Check size={11} strokeWidth={1.8} />
            Recovered
          </span>
        ) : emailed ? (
          <div>
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] bg-[var(--mn-accent)] text-white px-2.5 py-1.5">
              <Send size={11} strokeWidth={1.8} />
              Email sent
            </span>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--mn-ink-2)] mt-1.5">
              {relativeTime(cart.email_sent_at)}
            </p>
          </div>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] border border-[var(--mn-border-strong)] text-[var(--mn-ink-2)] px-2.5 py-1.5">
            <Clock size={11} strokeWidth={1.8} />
            Pending
          </span>
        )}
      </div>
    </li>
  );
}
